import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { formatBytes } from './ProcessUtils';
import { getProcessRowStyles } from './ProcessStyles';
import {
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

/**
 * Row component with expandable details for process data
 * 
 * @param {Object} props - Component props
 * @param {Object} props.processData - Process data object
 * @param {boolean} props.isItemSelected - Whether this row is selected
 * @param {Function} props.onSelect - Selection handler function
 * @returns {JSX.Element} The row component
 */
function ProcessRow({ processData, isItemSelected, onSelect }) {
  const [open, setOpen] = useState(false);
  const { isDarkTheme } = useTheme();
  const navigate = useNavigate();
  const process = processData.Process;

  // Validate process data
  if (!process) {
    console.warn("ProcessRow received invalid processData", processData);
    return null; // Don't render if data is malformed
  }

  const isActive = processData.state === 'active';
  const styles = getProcessRowStyles(isDarkTheme, isActive);

  const handleClick = () => {
    setOpen(!open);
    if (onSelect) {
      onSelect(); 
    }
  };
  
  // Handle creating an alert for this process
  const handleCreateAlert = (event) => {
    // Stop propagation to prevent row expansion
    event.stopPropagation();
    
    // Get process name
    const processName = process.process_name || 'Unknown';
    
    // Store process data in localStorage for alert creation
    localStorage.setItem('quickAlertProcess', JSON.stringify({
      processName: processName,
      processId: process.process_id || '',
      timestamp: new Date().getTime()
    }));
    
    // Navigate to alert creation page
    navigate('/create-alert');
  };

  return (
    <React.Fragment>
      <TableRow sx={styles.row} hover onClick={handleClick} selected={isItemSelected} tabIndex={-1} key={process.process_id}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" sx={styles.expandIcon}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={styles.statusIndicator} title={isActive ? 'Active' : 'Inactive'} />
            <div>
              <div>{process.process_name || 'N/A'}</div>
              {process.process_id === 'NA' && processData.name && (
                <div style={{ fontSize: '0.8em', color: 'gray' }}>
                  Monitor: {processData.name}
                </div>
              )}
            </div>
            <Tooltip title="Create alert for this process">
              <IconButton 
                size="small" 
                onClick={handleCreateAlert}
                sx={{
                  marginLeft: '8px',
                  color: isDarkTheme ? '#90caf9' : '#1976d2',
                  '&:hover': {
                    backgroundColor: isDarkTheme ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                  }
                }}
                aria-label="create alert"
              >
                <NotificationsActiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell align="right">{process.process_id}</TableCell>
        <TableCell align="right">{formatBytes(process.memory_usage)}</TableCell>
        <TableCell align="right">{process.cpu_usage ?? 'N/A'}%</TableCell>
        <TableCell align="right">{formatBytes(process.disk_usage)}</TableCell>
      </TableRow>

      {/* Expandable details section */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={styles.detailsContainer}>
              <Typography variant="h6" gutterBottom component="div">
                Details for {process.process_name} {process.process_id === 'NA' ? 
                  `(Monitor: ${processData.name || 'unknown'})` : 
                  `(PID: ${process.process_id})`}
              </Typography>
              <Table size="small" aria-label="process details">
                <TableBody>
                  {process.process_id === 'NA' && (
                    <TableRow><TableCell style={{ fontWeight: 'bold' }}>Monitor Name:</TableCell><TableCell>{processData.name || 'N/A'}</TableCell></TableRow>
                  )}
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Path:</TableCell><TableCell>{process.process_path || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Command:</TableCell><TableCell>{process.command_line || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>User:</TableCell><TableCell>{process.user_name || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Group:</TableCell><TableCell>{process.group_name || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Start Time:</TableCell><TableCell>{process.start_time || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>End Time:</TableCell><TableCell>{process.end_time || 'N/A'}</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Host:</TableCell><TableCell>{processData.host_name || 'N/A'} ({processData.host_ip || 'N/A'})</TableCell></TableRow>
                  <TableRow><TableCell style={{ fontWeight: 'bold' }}>Event Time:</TableCell><TableCell>{processData.event_time || 'N/A'}</TableCell></TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default ProcessRow;
